import type { AcquisitionSourceFilter } from "../acquisition/acquisitionSourceFilter";

/** The 5 acquisition metrics surfaced per state by the backend. */
export type UserAnalyticsByStateMetric =
  | "new_users"
  | "ftd_count"
  | "ftd_amount"
  | "avg_ftd_amount"
  | "total_users";

/** All 5 numeric fields the backend emits on every row. */
export type UserAnalyticsByStateMetrics = Record<
  UserAnalyticsByStateMetric,
  number
>;

/**
 * Filter descriptor surfaced in `meta.filters[*]`. The chart drives the
 * displayed metric purely client-side (every row already carries all 5
 * values), but the descriptor is kept so a server-side filter UI can be
 * wired later.
 */
export type UserAnalyticsByStateFilterMeta = {
  key: string;
  multi_select: boolean;
  options: string[];
  default_values: string[];
  selected_values: string[];
};

/** v2 ApiResponse envelope — axios `response.data` shape. */
export type UserAnalyticsByStateHttpBody = {
  data: Array<
    Partial<UserAnalyticsByStateMetrics> & {
      state?: string | null;
      [k: string]: unknown;
    }
  >;
  meta?: {
    request_id?: string | null;
    filters?: UserAnalyticsByStateFilterMeta[];
  };
};

/**
 * Normalized result consumed by the hook / UI. Keyed by USPS abbreviation so
 * the component can do O(1) lookups while densifying the 50-state list.
 */
export type UserAnalyticsByStateResult = {
  byAbbr: Record<string, UserAnalyticsByStateMetrics>;
  /** Pass-through of `meta.filters` (untouched), or empty if absent. */
  filters: UserAnalyticsByStateFilterMeta[];
};

/** Inputs accepted by the fetcher + hook. */
export type UserAnalyticsByStateQueryParams = AcquisitionSourceFilter & {
  start_date: string;
  end_date: string;
};

/**
 * All-zero metric tuple. Shared by the fetcher (default for malformed rows)
 * and the consumer component (fallback when a state is absent from the API
 * response — the geographic map paints `amount === 0` as "Unavailable" gray).
 * Frozen so consumers cannot mutate the shared instance.
 */
export const USER_ANALYTICS_BY_STATE_ZERO_METRICS: Readonly<UserAnalyticsByStateMetrics> =
  Object.freeze({
    new_users: 0,
    ftd_count: 0,
    ftd_amount: 0,
    avg_ftd_amount: 0,
    total_users: 0,
  });
